import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Delegation, DelegationAddEdit } from 'src/app/delegations/models/delegation';
import { DelegationTypeOptions } from 'src/app/delegations/models/delegation-type.enum';
import { selectDelegationEmployees } from 'src/app/delegations/store/selectors/delegations.selectors';
import { DelegationUser } from 'src/app/delegations/models/delegation-user';
import {
    clearDelegationEmployees,
    searchDelegationEmployees
} from 'src/app/delegations/store/actions/delegations.actions';
import { DelegationsState } from 'src/app/delegations/store/delegations.state';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import minMax from 'dayjs/plugin/minMax';
import { NovaValidators } from 'src/app/shared/validators/nova.validators';
dayjs.extend(utc);
dayjs.extend(minMax);

@UntilDestroy()
@Component({
    selector: 'nova-delegation-add-edit-dialog',
    standalone: true,
    imports: [
        NgFor,
        NgIf,
        AsyncPipe,
        MatDialogModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatAutocompleteModule,
        MatListModule,
        MatDatepickerModule,
        MatNativeDateModule,
        FormsModule,
        ReactiveFormsModule
    ],
    templateUrl: './delegation-add-edit-dialog.component.html',
    styleUrls: ['./delegation-add-edit-dialog.component.scss']
})
export class DelegationAddEditDialogComponent implements OnInit {
    readonly delegationTypeOptions = DelegationTypeOptions;
    _selectedEmployee: DelegationUser;
    startOfDay: Date;

    delegation: Delegation;
    ownerAspNetUserId: string;
    employees$: Observable<DelegationUser[]>;
    delegationFormGroup: UntypedFormGroup;

    @ViewChild('employeeSearchInput')
    employeeSearchInput: ElementRef<HTMLInputElement>;

    constructor(
        @Inject(MAT_DIALOG_DATA) private readonly _data: { delegation: Delegation; ownerAspNetUserId: string },
        private readonly _store: Store<DelegationsState>,
        private readonly _formBuilder: UntypedFormBuilder,
        private readonly _matDialogRef: MatDialogRef<DelegationAddEditDialogComponent>
    ) {
        this.delegation = this._data.delegation;
        this.ownerAspNetUserId = this._data.ownerAspNetUserId;
    }

    ngOnInit(): void {
        this._store.dispatch(clearDelegationEmployees());

        this.buildForm();

        this.employees$ = this._store.select(selectDelegationEmployees);

        this.delegationFormGroup
            .get('employeeSearch')
            .valueChanges.pipe(debounceTime(300), untilDestroyed(this))
            .subscribe((value) => {
                if (value?.fullName) {
                    this._store.dispatch(searchDelegationEmployees({ query: value?.fullName }));
                } else {
                    this._store.dispatch(searchDelegationEmployees({ query: value }));
                }
            });
    }

    buildForm() {
        this.startOfDay = dayjs().startOf('day').toDate();

        this.delegationFormGroup = this._formBuilder.group({
            employeeSearch: this._formBuilder.control(''),
            delegationStartDate: this._formBuilder.control(new Date(), [Validators.required]),
            delegationEndDate: this._formBuilder.control('', NovaValidators.minDate(this.startOfDay)),
            delegationTypes: this._formBuilder.control(null, [Validators.required]),
            selectedEmployeeId: this._formBuilder.control(null, [Validators.required])
        });

        if (this.delegation) {
            this.delegationFormGroup.patchValue(this.delegation);
            this.delegationFormGroup.controls.delegationStartDate.patchValue(
                dayjs(`${this.delegation.delegationStartDate}Z`).toDate()
            );
            if (this.delegation.delegationEndDate) {
                this.delegationFormGroup.controls.delegationEndDate.patchValue(
                    dayjs(`${this.delegation.delegationEndDate}Z`).toDate()
                );
            }
            this.delegationFormGroup.controls.delegationTypes.patchValue(
                this.delegation.delegationTypes.map((t) => t.key)
            );
            this.delegationFormGroup.controls.selectedEmployeeId.patchValue(this.delegation.delegatedUser.aspNetUserId);
        }
    }

    get titleCaption(): string {
        return this.delegation ? 'Edit coverage' : 'Set up coverage';
    }

    get saveCaption(): string {
        return this.delegation ? 'Save' : 'Set up coverage';
    }

    saveDelegation(): void {
        if (this.delegationFormGroup.valid) {
            const formValue = this.delegationFormGroup.value;
            const currentTime = dayjs().format('THH:mm:ss');
            const startDate = dayjs(formValue.delegationStartDate).format('YYYY-MM-DD');
            const startDateUtc = dayjs(`${startDate}${currentTime}`).toDate();
            let endDateUtc: Date;
            if (formValue.delegationEndDate) {
                const endDate = dayjs(formValue.delegationEndDate).format('YYYY-MM-DD');
                endDateUtc = dayjs(`${endDate}${currentTime}`).toDate();
            }
            const result: DelegationAddEdit = {
                id: this.delegation?.id,
                delegatedAspNetUserId: formValue.selectedEmployeeId,
                delegationStartDate: startDateUtc,
                delegationEndDate: endDateUtc,
                ownerAspNetUserId: this._data.ownerAspNetUserId,
                delegationTypeIds: formValue.delegationTypes
            };
            this._matDialogRef.close(result);
        }
    }

    employeeDisplay(employee: DelegationUser): string {
        return employee?.fullName;
    }

    employeeSelected(option: MatAutocompleteSelectedEvent): void {
        this._selectedEmployee = option.option.value;
        this.delegationFormGroup.get('selectedEmployeeId').patchValue(this._selectedEmployee.aspNetUserId ?? null);
    }

    getStartMaxDateValue(): Date | undefined {
        return this.delegationFormGroup.get('delegationEndDate').value;
    }

    getEndMinDateValue(): Date | undefined {
        return dayjs
            .max(dayjs(this.delegationFormGroup.get('delegationStartDate').value), dayjs(this.startOfDay))
            .toDate();
    }
}
